/**
 * BONSAI PAPERS — ANIMATION & INTERACTION ENGINE
 * ──────────────────────────────────────────────
 * Features:
 *  1. Intersection Observer scroll reveal (.reveal-on-scroll)
 *  2. Header scroll state (bp-scrolled class on <body>)
 *  3. Testimonials carousel (prev/next/dots/auto-advance)
 *  4. Gallery thumbnail switcher
 *  5. Qty selector
 *  6. FAQ accordion keyboard support
 *  7. Smooth parallax on hero image
 *  8. Email form success toast
 *  9. Marquee pause-on-hover
 */

(function () {
  'use strict';

  /* ────────────────────────────────────────────────
     1. SCROLL REVEAL (Intersection Observer)
     ──────────────────────────────────────────────── */
  function initScrollReveal() {
    const targets = document.querySelectorAll('.reveal-on-scroll');

    if (!('IntersectionObserver' in window)) {
      targets.forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    targets.forEach(el => observer.observe(el));
  }

  /* ────────────────────────────────────────────────
     2. HEADER SCROLL STATE
     ──────────────────────────────────────────────── */
  function initHeaderScroll() {
    const THRESHOLD = 80;
    let ticking = false;

    function updateScrollState() {
      if (window.scrollY > THRESHOLD) {
        document.body.classList.add('bp-scrolled');
      } else {
        document.body.classList.remove('bp-scrolled');
      }
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollState);
        ticking = true;
      }
    }, { passive: true });

    updateScrollState();
  }

  /* ────────────────────────────────────────────────
     3. TESTIMONIALS CAROUSEL
     ──────────────────────────────────────────────── */
  function initTestimonials() {
    const carousels = document.querySelectorAll('[data-testimonials]');

    carousels.forEach(carousel => {
      const slides = Array.from(carousel.querySelectorAll('.bp-testimonials__slide'));
      const dots   = Array.from(carousel.querySelectorAll('.bp-testimonials__dot'));
      const prevBtn = carousel.querySelector('[data-prev]');
      const nextBtn = carousel.querySelector('[data-next]');

      if (slides.length === 0) return;

      let current = 0;
      let timer = null;

      function goTo(index) {
        slides[current].classList.remove('is-active');
        if (dots[current]) dots[current].classList.remove('is-active');

        current = (index + slides.length) % slides.length;

        slides[current].classList.add('is-active');
        if (dots[current]) dots[current].classList.add('is-active');
      }

      function startAutoPlay() {
        timer = setInterval(() => goTo(current + 1), 5500);
      }

      function stopAutoPlay() {
        clearInterval(timer);
      }

      if (prevBtn) prevBtn.addEventListener('click', () => { stopAutoPlay(); goTo(current - 1); startAutoPlay(); });
      if (nextBtn) nextBtn.addEventListener('click', () => { stopAutoPlay(); goTo(current + 1); startAutoPlay(); });

      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => { stopAutoPlay(); goTo(i); startAutoPlay(); });
      });

      let touchStartX = 0;
      carousel.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
      carousel.addEventListener('touchend', e => {
        const diff = touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
          stopAutoPlay();
          goTo(current + (diff > 0 ? 1 : -1));
          startAutoPlay();
        }
      }, { passive: true });

      goTo(0);
      startAutoPlay();

      carousel.addEventListener('mouseenter', stopAutoPlay);
      carousel.addEventListener('mouseleave', startAutoPlay);
    });
  }

  /* ────────────────────────────────────────────────
     4. PRODUCT GALLERY (thumbnail switcher)
     ──────────────────────────────────────────────── */
  function initGallery() {
    const galleries = document.querySelectorAll('[data-bp-gallery]');

    galleries.forEach(gallery => {
      const mainImg   = gallery.querySelector('[data-bp-main-img]');
      const thumbs    = Array.from(gallery.querySelectorAll('[data-bp-thumb]'));

      if (!mainImg || thumbs.length === 0) return;

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const src = thumb.dataset.bpSrc;
          if (!src) return;

          mainImg.style.opacity = '0';
          mainImg.style.transition = 'opacity 0.25s ease';

          setTimeout(() => {
            mainImg.src = src;
            mainImg.style.opacity = '1';
          }, 250);

          thumbs.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    });
  }

  /* ────────────────────────────────────────────────
     5. QTY SELECTOR
     ──────────────────────────────────────────────── */
  function initQtySelectors() {
    document.querySelectorAll('[data-bp-qty]').forEach(wrapper => {
      const input   = wrapper.querySelector('input[type="number"], [data-qty-input]');
      const minusBtn = wrapper.querySelector('[data-minus]');
      const plusBtn  = wrapper.querySelector('[data-plus]');

      if (!input) return;

      function clampValue(v) {
        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max) || 99;
        return Math.max(min, Math.min(max, v));
      }

      if (minusBtn) {
        minusBtn.addEventListener('click', () => {
          input.value = clampValue(parseInt(input.value || 1) - 1);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      if (plusBtn) {
        plusBtn.addEventListener('click', () => {
          input.value = clampValue(parseInt(input.value || 0) + 1);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      input.addEventListener('change', () => {
        input.value = clampValue(parseInt(input.value) || 1);
      });
    });
  }

  /* ────────────────────────────────────────────────
     6. FAQ ACCORDION SUPPORT
     ──────────────────────────────────────────────── */
  function initFAQKeyboard() {
    document.querySelectorAll('summary').forEach(summary => {
      summary.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          summary.parentElement.open = !summary.parentElement.open;
        }
      });
    });
  }

  /* ────────────────────────────────────────────────
     7. HERO PARALLAX
     ──────────────────────────────────────────────── */
  function initHeroParallax() {
    const hero = document.querySelector('.bp-hero');
    const heroBg = hero?.querySelector('.bp-hero__bg img');
    if (!heroBg) return;

    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          const heroH = hero.offsetHeight;
          if (scrollY < heroH) {
            const offset = scrollY * 0.25;
            heroBg.style.transform = `translate3d(0, ${offset}px, 0) scale(1.04)`;
          }
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ────────────────────────────────────────────────
     8. EMAIL FORM SUCCESS TOAST
     ──────────────────────────────────────────────── */
  function initEmailForm() {
    const forms = document.querySelectorAll('.bp-email-signup__form');

    forms.forEach(form => {
      form.addEventListener('submit', e => {
        const input = form.querySelector('.bp-email-signup__input');
        if (!input || !input.value.includes('@')) return;

        const toast = document.createElement('div');
        toast.style.cssText = `
          position: fixed; bottom: 32px; left: 50%; transform: translateX(-50%);
          background: #1A1A18; color: #fff; font-family: 'Inter', sans-serif;
          font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
          padding: 14px 28px; z-index: 9999;
          opacity: 0; transition: opacity 0.4s ease;
        `;
        toast.textContent = 'Thank you. Welcome to Bonsai Papers.';
        document.body.appendChild(toast);

        requestAnimationFrame(() => { toast.style.opacity = '1'; });
        setTimeout(() => {
          toast.style.opacity = '0';
          setTimeout(() => toast.remove(), 500);
        }, 3500);
      });
    });
  }

  /* ────────────────────────────────────────────────
     9. MARQUEE CLICK-TO-PAUSE
     ──────────────────────────────────────────────── */
  function initMarquee() {
    document.querySelectorAll('.bp-marquee, .bp-announcement').forEach(el => {
      let paused = false;
      const inner = el.querySelector('.bp-marquee__content, .bp-announcement__inner');
      if (!inner) return;

      el.addEventListener('click', () => {
        paused = !paused;
        inner.style.animationPlayState = paused ? 'paused' : 'running';
      });
    });
  }

  /* ────────────────────────────────────────────────
     INIT
     ──────────────────────────────────────────────── */
  function init() {
    initScrollReveal();
    initHeaderScroll();
    initTestimonials();
    initGallery();
    initQtySelectors();
    initFAQKeyboard();
    initHeroParallax();
    initEmailForm();
    initMarquee();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
